import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertCircle, Info, X, Bug } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useLanguage } from './LanguageContext';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const { language } = useLanguage();
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => {
    const id = Math.random().toString(36).substr(2, 9);
    const duration = type === 'bug' ? 6000 : 4000;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div className="fixed top-[72px] right-1 z-[9999] flex flex-col gap-3 pointer-events-none transition-all duration-300">
          <AnimatePresence>
            {toasts.map((toast) => (
              <motion.div
                key={toast.id}
                initial={{ opacity: 0, x: 50, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 20, scale: 0.95 }}
                className="pointer-events-auto"
              >
                <div className={`${
                  toast.type === 'bug' 
                    ? 'p-0 shadow-none' 
                    : 'bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-4 pr-12 shadow-2xl'
                } relative min-w-[280px] flex items-center gap-3 overflow-hidden group`}>
                  {toast.type === 'bug' ? (
                    <div className="flex flex-row items-center gap-0 p-2 group transition-all duration-300">
                      {/* Bug Icon - Left Side */}
                      <div className="flex flex-col items-center shrink-0 translate-y-[-2px] group-hover:scale-110 group-hover:-rotate-12 transition-all z-10">
                        <div className="p-0 relative">
                          <img 
                            src="/bug_toast.png" 
                            alt="Bug" 
                            className="w-12 h-12 object-contain animate-pulse animate-bounce-subtle"
                            style={{ filter: 'drop-shadow(0 0 8px rgba(220, 38, 38, 0.8)) sepia(1) hue-rotate(-50deg) saturate(20) brightness(0.8)' }}
                          />
                          
                          {/* Minimalist Floating Close Button */}
                          <button 
                            onClick={() => removeToast(toast.id)}
                            className="absolute -top-1 -right-1 p-1 bg-black/80 hover:bg-rose-600 border border-white/10 rounded-full text-white/40 hover:text-white transition-all opacity-0 group-hover:opacity-100 shadow-xl"
                          >
                            <X size={8} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Comical Speech Bubble - Right Side, separated from icon */}
                      <div className="relative bg-black border-2 border-white/20 rounded-[20px] p-3 pl-5 shadow-2xl min-w-[120px] max-w-[200px] ml-4 motion-safe:animate-bounce-subtle">
                        {/* Speech Bubble Tail - Pointing left to the bug icon */}
                        <div className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-black border-l-2 border-b-2 border-white/20 rotate-45" />
                        
                        <p className="text-[12px] font-bold text-rose-50/90 leading-tight italic tracking-tight select-none drop-shadow-sm text-center">
                          "{toast.message}"
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                        toast.type === 'success' ? 'bg-emerald-500' : 
                        toast.type === 'error' ? 'bg-rose-500' : 'bg-blue-500'
                      }`} />
                      
                      <div className={`p-2 rounded-xl ${
                        toast.type === 'success' ? 'bg-emerald-500/10 text-emerald-500' : 
                        toast.type === 'error' ? 'bg-rose-500/10 text-rose-500' : 'bg-blue-500/10 text-blue-500'
                      }`}>
                        {toast.type === 'success' ? <CheckCircle2 size={18} /> : 
                         toast.type === 'error' ? <AlertCircle size={18} /> : <Info size={18} />}
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/30 mb-0.5">
                          Notificación
                        </p>
                        <p className="text-sm font-bold text-white leading-relaxed">
                          {toast.message}
                        </p>
                      </div>

                      <button 
                        onClick={() => removeToast(toast.id)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-white/5 rounded-lg text-slate-500 hover:text-white transition-all opacity-0 group-hover:opacity-100"
                      >
                        <X size={14} />
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};
